import type { Zone } from '@seek/audience-zones';
import { JobDetailsView } from '@seek/job-details-view';
import { Box, ContentBlock } from 'braid-design-system';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Lmis, { useIsNullLmis } from 'src/components/Lmis/Lmis';
import { ScreenReaderSkipTarget } from 'src/components/ScreenReaderSkipTarget/ScreenReaderSkipTarget';
import { HomePage } from 'src/handlers/Home';
import { SearchResultPage } from 'src/handlers/SearchResults';
import { storeLastJobId } from 'src/hooks/useLastJobId';
import {
  useStopTimerOnComponentVisible,
  useStopTimerOnComponentVisibleControl,
} from 'src/modules/hooks/useStopTimerOnComponentVisible';
import type { JobDetails } from 'src/store/jobdetails/types';
import { useSelector } from 'src/store/react';
import { selectFeatureFlag } from 'src/store/selectors';
import { useMelwaysZone } from 'src/utils/melwaysHelper';

import { useSplitViewAccessibilityLinks } from '../SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';

import { buildJobPostingMetadata } from './buildJobPostingMetadata';
import { usePropsForJobDetailsView } from './usePropsForJobDetailsView';

export const JOB_DETAILS_PAGE_METRICS_IDENTIFIER = 'jobDetailsPage';

interface JobDetailsPageProps {
  jobDetails: JobDetails;
  embedded?: boolean;
}

const blockAdvertisersForGFJ: string[] = ['test-id'];
type SupportedZone = {
  zone: string;
  launchDate?: number;
};
const releaseZonesAndLaunchDatesForGFJ: SupportedZone[] = [
  { zone: 'asia-7' },
  { zone: 'asia-6' },
];

const validateZoneAndLaunchDate = (
  currentZone: string,
  listedAtDate: number,
) => {
  const zoneAndLaunchDate = releaseZonesAndLaunchDatesForGFJ.find(
    ({ zone: launchZone }) => currentZone === launchZone,
  );
  if (!zoneAndLaunchDate) {
    return false;
  }
  const { launchDate } = zoneAndLaunchDate;
  if (launchDate) {
    return listedAtDate >= launchDate;
  }
  return true;
};
export const shouldRenderGfj = (
  advertiserId: string,
  listedAtDate: number,
  isExpired: boolean,
  zone: Zone,
  sourceZone?: Zone,
) => {
  const disableForGFJ = blockAdvertisersForGFJ.includes(advertiserId);

  return (
    sourceZone !== null &&
    sourceZone === zone &&
    !disableForGFJ &&
    validateZoneAndLaunchDate(zone, listedAtDate) &&
    !isExpired
  );
};

const JobDetailsEmbeddedMetrics = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useStopTimerOnComponentVisibleControl({
    actionName: 'JobListItemClickToJDV:Visible:Control',
  });
  const ref = useStopTimerOnComponentVisible({
    actionName: 'JobListItemClickToJDV:Visible',
  });

  return <Box ref={ref}>{children}</Box>;
};

const useGetPropsOptions = (
  embedded?: boolean,
): Parameters<typeof usePropsForJobDetailsView>[0]['options'] => {
  const enableStandalone = useSelector(
    selectFeatureFlag('competitivePlacement'),
  );
  const enableSplitView = useSelector(
    selectFeatureFlag('competitivePlacementEmbeddedJdv'),
  );
  return {
    competitivePlacement: {
      openInNewTab: embedded || false,
      enabled: (enableStandalone && !embedded) || (enableSplitView && embedded),
    },
  };
};

const JobDetailsPage = ({ jobDetails, embedded }: JobDetailsPageProps) => {
  const options = useGetPropsOptions(embedded);
  const props = usePropsForJobDetailsView({ jobDetails, options });
  const isNullLmis = useIsNullLmis({});
  const zone = useMelwaysZone();

  const { advertiser, listedAt, isExpired, id, sourceZone } = jobDetails.job;

  const renderGfj = shouldRenderGfj(
    advertiser.id,
    Date.parse(String(listedAt?.dateTimeUtc)),
    isExpired,
    zone,
    sourceZone as Zone,
  );

  const jobPostedInDifferentZone = useMemo(
    () => !sourceZone || sourceZone !== zone,
    [sourceZone, zone],
  );

  const jobPostingMetadata = useMemo(
    () => buildJobPostingMetadata(jobDetails),
    [jobDetails],
  );

  // State is used here to prevent a hydration error from happening
  // Saved jobs data is not fetched server side, so we can only update the save button client side
  const [isSaved, setIsSaved] = useState(false);
  useEffect(() => {
    if (props.job.isSaved !== undefined) {
      setIsSaved(props.job.isSaved);
    }
  }, [props.job.isSaved]);

  useEffect(() => {
    storeLastJobId(id);
  }, [id]);

  const propsWithIsSaved = {
    ...props,
    job: {
      ...props.job,
      isSaved,
    },
  };

  const lmis = isNullLmis ? undefined : <Lmis />;

  const { focusReturnToSearchLink } = useSplitViewAccessibilityLinks();
  useEffect(() => {
    focusReturnToSearchLink();
  }, [focusReturnToSearchLink]);

  // The recommended jobs that come from the JDV Graph

  const content = (
    <JobDetailsView
      {...propsWithIsSaved}
      careerInsights={lmis}
      hideJobLink={propsWithIsSaved.jobUrlOpts?.hide}
      embedded={embedded}
    />
  );

  useEffect(() => {
    HomePage.preload();
    SearchResultPage.preload();
  }, []);

  return embedded ? (
    <JobDetailsEmbeddedMetrics>
      <Box
        // Prevent share button from being cropped by scroll container
        // (only an issue when the job has no cover image or logo)
        paddingTop={
          props.job.branding?.assets.cover?.url ||
          props.job.branding?.assets.logo?.url
            ? undefined
            : 'xxsmall'
        }
        data={{ automation: 'jobDetailsPage' }}
      >
        {content}
      </Box>
    </JobDetailsEmbeddedMetrics>
  ) : (
    <Box
      // Provide space from header divider on desktop
      paddingTop={{ desktop: 'large' }}
      data={{
        automation: 'jobDetailsPage',
        ['metrics-identifier']: JOB_DETAILS_PAGE_METRICS_IDENTIFIER,
      }}
    >
      <ContentBlock width="medium">
        <ScreenReaderSkipTarget name="start-of-content" />
        {jobPostedInDifferentZone ? (
          <Helmet
            meta={[
              {
                name: 'robots',
                content: 'noindex,nofollow',
              },
            ]}
          />
        ) : null}
        {renderGfj ? (
          <Helmet
            script={[
              {
                type: 'application/ld+json',
                innerHTML: JSON.stringify(jobPostingMetadata),
              },
            ]}
          />
        ) : null}
        {content}
      </ContentBlock>
    </Box>
  );
};

export default JobDetailsPage;
